/* eslint-disable @typescript-eslint/no-explicit-any */
import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6 Vendor-Port W5 — COIL selector data layer (§33.2).
 *
 * The coil selector has TWO rate paths (§33.2):
 *   1. VENDOR  → POST /api/v1.0/rating/ {part:'coil', manufacturer:<derived>} —
 *      the EXTERNAL vendor dispatch, handled by the SHARED useRatingDispatch
 *      (ratingApi). NOT re-declared here.
 *   2. KSDM-DB → POST /ahu/rating_database_coil_selection/ {manufacturer:'manual',
 *      part:'coil', type, input} — the KSDM master-coil DB search (a NON-/api
 *      route, ahu/urls.py:24). Declared here as `postCoilDatabaseRating`. It is
 *      abortable (legacy self.rate_request.abort()).
 *
 * SELECT (apply) writes route through current_model auto-save (the form model
 * PATCH) + patchRatedCoil (rating_mode_coils/<id>) + section + project — all in
 * ahuComponentFormsApi / the form's model. This file declares only the
 * selector-specific READS + the DB-route rate mutation.
 *
 * ★ DATA-SAFETY: getCoilNotes is a GET. postCoilDatabaseRating fires ONLY from a
 * userArmed #rate click — NEVER on mount/open/browse. The read-only SMOKE fires
 * GETs only → 0 mutating + 0 /rating/ + 0 rating_database_coil_selection.
 */

export interface CoilNote {
  note?: string;
  text?: string;
  name?: string;
  [k: string]: unknown;
}

/** The §32-shaped always-200 body the DB route returns (same as /rating/). */
export interface CoilRatingResponse {
  ok?: boolean;
  models?: Array<Record<string, unknown>>;
  errors?: string[];
  warnings?: string[];
  notes?: unknown;
  cancelled?: boolean;
  [k: string]: unknown;
}

/** The DB-route envelope (manufacturer:'manual'). */
export interface CoilDbRatingEnvelope {
  manufacturer: 'manual';
  part: 'coil';
  type: string;
  input: Record<string, unknown>;
}

export const coilSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // coil rating notes (legacy NotesCollection; baseUrl carries ?component_mfg_id=).
    getCoilNotes: builder.query<CoilNote[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): CoilNote[] => {
        if (Array.isArray(res)) return res as CoilNote[];
        const o = res as { notes?: CoilNote[]; results?: CoilNote[] } | null;
        return o?.notes ?? o?.results ?? [];
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // generic master list by absolute URL (the ~40 coil catalogs — dependency
    // graph loads them via raw fetch, but the conn_ends/hands static lists fetch
    // here for cache reuse).
    getCoilMasterList: builder.query<any[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): any[] =>
        Array.isArray(res) ? res : ((res as any)?.results ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // KSDM-DB rate (manufacturer:'manual'): POST /ahu/rating_database_coil_selection/.
    // Always-200 (body carries ok/errors/warnings/models). NEVER auto-fired.
    postCoilDatabaseRating: builder.mutation<CoilRatingResponse, CoilDbRatingEnvelope>({
      query: (envelope) => ({
        url: `/ahu/rating_database_coil_selection/`,
        method: 'POST',
        body: envelope,
      }),
      // never cached: each rate is a fresh DB search.
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCoilNotesQuery,
  useGetCoilMasterListQuery,
  usePostCoilDatabaseRatingMutation,
} = coilSelectorApi;
